import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, LogIn, Sparkles, ChevronRight, ShieldCheck, Languages, Check, Sun, Moon, Trash2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";

export default function Settings() {
  const navigate = useNavigate();
  const { user, setUser, login, logout } = useAuth();
  const { t, lang, setLang, languages } = useLang();
  const { theme, setTheme } = useTheme();
  const [langOpen, setLangOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const current = languages.find((l) => l.code === lang);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      setUser(null);
      setDeleteOpen(false);
      toast.success(t("delete.deleted"));
      navigate("/");
    } catch (e) {
      toast.error(t("delete.error"));
      setDeleting(false);
    }
  };

  const choose = (code) => {
    setLang(code);
    setLangOpen(false);
    toast.success(t("toast.languageUpdated"));
  };

  return (
    <AppShell>
      <h1 className="text-3xl font-light tracking-tight text-decide-ink">{t("settings.title")}</h1>

      {user ? (
        <div className="mt-6 decide-card flex items-center gap-4" data-testid="account-card">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-12 w-12 rounded-full object-cover" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-decide-forest text-white font-semibold">{(user.name || user.email || "?").charAt(0).toUpperCase()}</div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-decide-ink truncate">{user.name || t("settings.you")}</p>
            <p className="text-[13px] text-decide-slate truncate">{user.email}</p>
          </div>
        </div>
      ) : (
        <div className="mt-6 decide-card text-center">
          <p className="text-decide-slate">{t("settings.signinPrompt")}</p>
          <button data-testid="settings-signin" onClick={login} className="btn-accent mt-4 w-full"><LogIn size={18} /> {t("nav.signinGoogle")}</button>
        </div>
      )}

      {/* Appearance */}
      <div className="mt-4 decide-card" data-testid="appearance-card">
        <div className="flex items-center gap-3">
          {theme === "dark" ? <Moon size={20} className="text-decide-forest" /> : <Sun size={20} className="text-decide-forest" />}
          <p className="font-semibold text-decide-ink">{t("settings.appearance")}</p>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 rounded-full border border-decide-line p-1">
          <button data-testid="theme-light" onClick={() => setTheme("light")} className={`flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors ${theme === "light" ? "bg-decide-forest text-white" : "text-decide-slate hover:text-decide-ink"}`}>
            <Sun size={16} /> {t("settings.light")}
          </button>
          <button data-testid="theme-dark" onClick={() => setTheme("dark")} className={`flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-colors ${theme === "dark" ? "bg-decide-forest text-decide-canvas" : "text-decide-slate hover:text-decide-ink"}`}>
            <Moon size={16} /> {t("settings.dark")}
          </button>
        </div>
      </div>

      {/* Language */}
      <button data-testid="language-row" onClick={() => setLangOpen(true)} className="mt-4 w-full decide-card flex items-center justify-between text-left transition-colors hover:border-decide-mute">
        <div className="flex items-center gap-3">
          <Languages size={20} className="text-decide-forest" />
          <div>
            <p className="font-semibold text-decide-ink">{t("settings.language")}</p>
            <p className="text-[13px] text-decide-slate">{t("settings.languageSub")}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 ml-3">
          <span className="text-sm font-medium text-decide-slate">{current?.native}</span>
          <ChevronRight size={18} className="text-decide-mute" />
        </div>
      </button>

      {/* Plan */}
      <div className="mt-4 decide-card" data-testid="plan-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-decide-forest" />
            <span className="font-semibold text-decide-ink">{user?.is_pro ? t("settings.pro") : t("settings.free")}</span>
          </div>
          {user?.is_pro ? (
            <span className="rounded-full bg-decide-forestLight px-2.5 py-1 text-[11px] font-bold text-decide-forest">{t("settings.active")}</span>
          ) : (
            <button data-testid="settings-upgrade" onClick={() => navigate("/pricing")} className="inline-flex items-center gap-1 text-sm font-semibold text-decide-forest">{t("settings.upgrade")} <ChevronRight size={15} /></button>
          )}
        </div>
        {user && !user.is_pro && (
          <p className="mt-3 text-[13px] text-decide-slate">{t("settings.freeLeft", { n: Math.max(0, user.free_limit - user.analyses_used), total: user.free_limit })}</p>
        )}
      </div>

      {/* Legal */}
      <button data-testid="privacy-row" onClick={() => navigate("/privacy")} className="mt-4 w-full decide-card flex items-center justify-between text-left transition-colors hover:border-decide-mute">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} className="text-decide-forest" />
          <div>
            <p className="font-semibold text-decide-ink">{t("settings.privacy")}</p>
            <p className="text-[13px] text-decide-slate">{t("settings.privacySub")}</p>
          </div>
        </div>
        <ChevronRight size={18} className="text-decide-mute shrink-0 ml-3" />
      </button>

      <button data-testid="terms-row" onClick={() => navigate("/terms")} className="mt-3 w-full decide-card flex items-center justify-between text-left transition-colors hover:border-decide-mute">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-decide-forest" />
          <p className="font-semibold text-decide-ink">{t("settings.terms")}</p>
        </div>
        <ChevronRight size={18} className="text-decide-mute shrink-0 ml-3" />
      </button>

      <div className="mt-4 rounded-2xl border border-decide-line bg-decide-surface p-4 flex items-start gap-3" data-testid="disclaimer-card">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-decide-slate" />
        <p className="text-[13px] leading-relaxed text-decide-slate">{t("settings.disclaimer")}</p>
      </div>

      {user && (
        <button data-testid="logout-btn" onClick={logout} className="btn-secondary mt-6 w-full"><LogOut size={17} /> {t("settings.signout")}</button>
      )}

      {user && (
        <button data-testid="delete-account-btn" onClick={() => setDeleteOpen(true)} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-decide-danger/40 bg-decide-dangerSoft px-6 py-3.5 font-semibold text-decide-danger transition-colors hover:bg-decide-danger hover:text-white active:scale-[0.98]">
          <Trash2 size={17} /> {t("settings.deleteAccount")}
        </button>
      )}

      <AnimatePresence>
        {langOpen && (
          <>
            <motion.div className="fixed inset-0 z-50 bg-black/30" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLangOpen(false)} />
            <motion.div
              data-testid="language-sheet"
              className="fixed bottom-0 inset-x-0 z-50 mx-auto max-w-lg bg-decide-canvas rounded-t-3xl border-t border-decide-line px-5 pt-3 pb-8"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}
            >
              <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-decide-line" />
              <h3 className="px-1 text-xl font-semibold text-decide-ink">{t("settings.language")}</h3>
              <div className="mt-4 space-y-1.5">
                {languages.map((l) => {
                  const active = l.code === lang;
                  return (
                    <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => choose(l.code)} className={`w-full flex items-center justify-between rounded-xl px-4 py-3.5 text-left transition-colors ${active ? "bg-decide-forestLight" : "hover:bg-decide-mist"}`}>
                      <span>
                        <span className="font-medium text-decide-ink">{l.native}</span>
                        <span className="ml-2 text-[13px] text-decide-mute">{l.label}</span>
                      </span>
                      {active && <Check size={18} className="text-decide-forest" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {deleteOpen && (
          <>
            <motion.div className="fixed inset-0 z-50 bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => !deleting && setDeleteOpen(false)} />
            <motion.div
              data-testid="delete-confirm-sheet"
              className="fixed bottom-0 inset-x-0 z-50 mx-auto max-w-lg bg-decide-canvas rounded-t-3xl border-t border-decide-line px-6 pt-3 pb-8"
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 2rem)" }}
            >
              <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-decide-line" />
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-decide-dangerSoft">
                <AlertTriangle size={22} className="text-decide-danger" />
              </div>
              <h3 className="mt-4 text-2xl font-semibold tracking-tight text-decide-ink">{t("delete.confirmTitle")}</h3>
              <p className="mt-2 text-decide-slate leading-relaxed">{t("delete.confirmBody")}</p>
              <button data-testid="confirm-delete-btn" disabled={deleting} onClick={confirmDelete} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-decide-danger px-6 py-3.5 font-semibold text-white transition-opacity active:scale-[0.98] disabled:opacity-50">
                <Trash2 size={17} /> {t("delete.confirmBtn")}
              </button>
              <button data-testid="cancel-delete-btn" disabled={deleting} onClick={() => setDeleteOpen(false)} className="btn-ghost mt-2 w-full">{t("delete.cancel")}</button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AppShell>
  );
}
